import {
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  query,
  addDoc,
  deleteDoc,
  orderBy,
  where,
} from "firebase/firestore";
import { firestore } from "firebaseConfig";
import moment from "moment";
import { sortArrayByName } from "utils/common";
import { CLIENT_NOT_FOUND } from "constant";
import { calcPriceWT } from "utils/common";
import { timestampConversionv1 } from "utils/common";
import { alternatives, date } from "joi";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { WHATSAPP_TEMPLATE_TYPES } from "constant";

//for getting client list
export const getClientList = async (uid) => {
  try {
    let data = [];
    const q = query(
      collection(firestore, "client"),
      where("adminId", "==", uid)
    );

    const querySnapshot = await getDocs(q);

    let yesterday = moment().subtract(1, "days");
    yesterday = yesterday.format("YYYY-MM-DD");

    querySnapshot.forEach((doc, index) => {
      let dates = [];
      let note = "";
      if (doc.data().dietPlan) {
        for (let j = 0; j < doc.data().dietPlan.length; j++) {
          dates.push(new Date(doc.data().dietPlan[j]?.date));
          let createdAt = doc.data().dietPlan[j].createdAt;
          if (createdAt.seconds) {
            createdAt = timestampConversionv1(createdAt);
            createdAt = moment(createdAt).format("YYYY-MM-DD");
          }

          if (yesterday === createdAt) {
            note = doc.data().dietPlan[j].notes;
          }
        }
      }
      data.push({
        name: doc.data().name,
        phoneNumber: doc.data().phoneNumber,
        label: doc.data().name + ` (+91 ${doc.data().phoneNumber})`,
        value: doc.id,
        key: doc.id,
        dietDates: dates,
        note: note,
        isClientTermsAccepted: doc.data().isClientTermsAccepted,
        email: doc.data().email,
        referredBy: doc.data().referredBy,
        preference: doc.data().preference ?? "",
      });
    });

    sortArrayByName(data);
    return data;
  } catch (e) {
    throw e;
  }
};

//for getting client diet chart details
export const getClient = async (id) => {
  try {
    const ref = doc(firestore, "client", id);
    let data;
    const snapshot = await getDoc(ref);
    if (snapshot.exists()) {
      data = snapshot.data();

      let updatedAt;
      let createdAt;
      if (snapshot.data().createdAt) {
        createdAt = snapshot.data().createdAt;
        createdAt =
          createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000;
        createdAt = moment.utc(createdAt);
      }

      if (snapshot.data().updatedAt) {
        updatedAt = snapshot.data().updatedAt;
        updatedAt =
          updatedAt?.nanoseconds / 1000000 + updatedAt?.seconds * 1000;
        updatedAt = moment.utc(updatedAt);
      }

      if (data.dietPlan) {
        data.dietPlan.sort(function (a, b) {
          let createdAt1 = a.createdAt;
          createdAt1 = createdAt1.seconds
            ? createdAt1?.nanoseconds / 1000000 + createdAt1?.seconds * 1000
            : createdAt1;

          let createdAt2 = b.createdAt;
          createdAt2 = createdAt2.seconds
            ? createdAt2?.nanoseconds / 1000000 + createdAt2?.seconds * 1000
            : createdAt2;
          return new Date(createdAt2) - new Date(createdAt1);
        });

        for (let j = 0; j < data.dietPlan.length; j++) {
          let keys = Object.keys(data.dietPlan[j]);

          for (let i = 0; i < keys.length; i++) {
            if (
              keys[i] !== "date" &&
              keys[i] !== "notes" &&
              keys[i] !== "clientName" &&
              keys[i] !== "createdAt" &&
              keys[i] !== "dietChartId" &&
              keys[i] !== "weekPlan" &&
              keys[i] !== "chartType"
            ) {
              if (!Array.isArray(data.dietPlan[j][keys[i]])) {
                data.dietPlan[j][keys[i]] = [data.dietPlan[j][keys[i]]];
              }
            }
          }
        }
      }

      return { ...data, updatedAt, createdAt, id: id };
    } else {
      return CLIENT_NOT_FOUND;
    }
  } catch (e) {
    throw e;
  }
};

const DIET_PLAN_META_KEYS = new Set([
  "from",
  "to",
  "date",
  "notes",
  "clientName",
  "dietChartId",
  "weekPlan",
  "chartType",
  "createdAt",
  "clientId",
  "layoutVariant",
  "dayPlans",
  "dayScheduleKeys",
]);

function collectRecipeIdsFromDayPlans(dayPlans, recipeIds) {
  if (!dayPlans || typeof dayPlans !== "object") return;
  Object.values(dayPlans).forEach((dayObj) => {
    if (!dayObj || typeof dayObj !== "object") return;
    Object.values(dayObj).forEach((arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((recipe) => {
        if (recipe && recipe.id) recipeIds.add(recipe.id);
      });
    });
  });
}

async function enrichDietChartsWithGroceryList(data) {
  const recipeIds = new Set();
  data.forEach((chart) => {
    if (chart.layoutVariant === "perDayRange" && chart.dayPlans) {
      collectRecipeIdsFromDayPlans(chart.dayPlans, recipeIds);
    }
    Object.keys(chart).forEach((key) => {
      if (DIET_PLAN_META_KEYS.has(key)) return;
      const arr = chart[key];
      if (!Array.isArray(arr)) return;
      arr.forEach((recipe) => {
        if (recipe && recipe.id) recipeIds.add(recipe.id);
      });
    });
  });
  if (recipeIds.size === 0) return data;
  const ids = Array.from(recipeIds);
  const idToGrocery = {};
  const batchSize = 10;
  for (let i = 0; i < ids.length; i += batchSize) {
    const batch = ids.slice(i, i + batchSize);
    const snaps = await Promise.all(
      batch.map((id) => getDoc(doc(firestore, "recipe", id)))
    );
    batch.forEach((id, idx) => {
      const snap = snaps[idx];
      if (snap && snap.exists()) {
        const gl = snap.data().groceryList;
        idToGrocery[id] = Array.isArray(gl) ? gl : [];
      }
    });
  }
  data.forEach((chart) => {
    if (chart.layoutVariant === "perDayRange" && chart.dayPlans) {
      Object.keys(chart.dayPlans).forEach((dateKey) => {
        const dayObj = chart.dayPlans[dateKey];
        if (!dayObj) return;
        Object.keys(dayObj).forEach((sk) => {
          const arr = dayObj[sk];
          if (!Array.isArray(arr)) return;
          arr.forEach((recipe) => {
            if (
              recipe &&
              recipe.id &&
              idToGrocery.hasOwnProperty(recipe.id)
            ) {
              recipe.groceryList = idToGrocery[recipe.id];
            }
          });
        });
      });
    }
    Object.keys(chart).forEach((key) => {
      if (DIET_PLAN_META_KEYS.has(key)) return;
      const arr = chart[key];
      if (!Array.isArray(arr)) return;
      arr.forEach((recipe) => {
        if (recipe && recipe.id && idToGrocery.hasOwnProperty(recipe.id)) {
          recipe.groceryList = idToGrocery[recipe.id];
        }
      });
    });
  });
  return data;
}

export const getClientDietChart = async (uid) => {
  try {
    const q = query(
      collection(firestore, "dietplan"),
      where("clientId", "==", uid),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    let data = [];

    querySnapshot.forEach((docSnap) => {
      data.push({ ...docSnap.data(), dietChartId: docSnap.id });
    });

    return await enrichDietChartsWithGroceryList(data);
  } catch (e) {
    throw e;
  }
};

//get plan for dates
export const getClientDietChartForDate = async (uid) => {
  try {
    const q = query(
      collection(firestore, "dietplan"),
      where("clientId", "==", uid),
      orderBy("createdAt", "desc")
    );
    const querySnapshot = await getDocs(q);

    let data = [];
    let note = null;

    querySnapshot.forEach((doc) => {
      const docData = doc.data();
      if (note === null) {
        note = docData.notes;
      }
      if (docData.date) {
        data.push({ date: new Date(docData.date), id: doc.id });
      }

      if (docData.from && docData.to) {
        data.push({
          // from: new Date(docData.from),
          // to: new Date(docData.to),
          date: new Date(docData.from),
          id: doc.id,
        });
      }
    });

    return { data, note };
  } catch (e) {
    throw e;
  }
};

//for user recipes
export const getUserRecipes = async (uid) => {
  try {
    let data = [];
    const q = query(
      collection(firestore, "recipe"),
      where("adminId", "==", uid)
    );

    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      let timestamp = null;
      let updatedAt = null;
      if (doc.data().createdAt) {
        let createdAt = doc.data().createdAt;
        timestamp =
          createdAt?.nanoseconds / 1000000 + createdAt?.seconds * 1000;
        timestamp = moment.utc(timestamp);
      }

      if (doc.data().updatedAt) {
        updatedAt = doc.data().updatedAt;
        updatedAt =
          updatedAt?.nanoseconds / 1000000 + updatedAt?.seconds * 1000;
        updatedAt = moment.utc(updatedAt);
      }

      data.push({
        ...doc.data(),
        createdAt: timestamp,
        id: doc.id,
        updatedAt,
      });
    });

    data.sort(function (a, b) {
      let createdAt1 = a.createdAt._d;
      createdAt1 = createdAt1.seconds
        ? createdAt1?.nanoseconds / 1000000 + createdAt1?.seconds * 1000
        : createdAt1;

      let createdAt2 = b.createdAt._d;
      createdAt2 = createdAt2.seconds
        ? createdAt2?.nanoseconds / 1000000 + createdAt2?.seconds * 1000
        : createdAt2;
      return new Date(createdAt2) - new Date(createdAt1);
    });

    return [...data];
  } catch (e) {
    throw e;
  }
};

//Remove diet of client
export const RemoveDiet = async (dietChartId) => {
  try {
    await deleteDoc(doc(firestore, "dietplan", dietChartId));
  } catch (e) {
    throw new Error("Something went wrong, retry");
  }
};

//get service list
export const getServiceInventory = async (uid) => {
  try {
    let data = [];
    const q = query(
      collection(firestore, "service"),
      where("adminId", "==", uid)
    );
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      let dataObj = {
        label: doc.data().serviceName,
        value: doc.data().serviceName,
        totalAmount: parseFloat(
          calcPriceWT(doc.data().sellingPriceWOT, doc.data().gst)
        ),
        key: doc.id,
      };
      data.push(dataObj);
    });

    return [...data];
  } catch (e) {
    throw e;
  }
};

//Add diet chart of client
export const AddDiet = async ({ formdata, dietPlan, uid }) => {
  try {
    const storage = getStorage();
    let id = formdata.clientName.value;
    let clientDietPlan = {
      clientId: id,
      date: formdata.from || dietPlan.from,
      from: dietPlan.from,
      to: dietPlan.to,
      notes: dietPlan.notes,
      weekPlan: dietPlan.weekPlan.value,
      chartType: dietPlan.chartType.value,
      createdAt: new Date(),
    };

    let keys = Object.keys(dietPlan);
    for (let i = 0; i < keys.length; i++) {
      if (
        keys[i] !== "from" &&
        keys[i] !== "to" &&
        keys[i] !== "date" &&
        keys[i] !== "notes" &&
        keys[i] !== "clientName" &&
        keys[i] !== "dietChartId" &&
        keys[i] !== "weekPlan" &&
        keys[i] !== "chartType" &&
        keys[i] !== "createdAt" &&
        keys[i] !== "clientId" &&
        keys[i] !== "dietChartId"
      ) {
        for (let j = 0; j < dietPlan[keys[i]].length; j++) {
          let recipe = dietPlan[keys[i]];
          if (!dietPlan[keys[i]][j]["id"] && dietPlan[keys[i]][j]["name"]) {
            const recipeName =
              typeof recipe[j].name === "object"
                ? recipe[j].name.value
                : recipe[j].name;

            if (!recipeName || typeof recipeName !== "string") {
              continue;
            }
            const q = query(
              collection(firestore, "recipe"),
              where("name", "==", recipeName),
              where("adminId", "==", uid)
            );
            const querySnapshotv1 = await getDocs(q);
            if (!querySnapshotv1.empty) {
              throw new Error({
                message: `Recipe with this name ${recipe[j].name.value} already exists, choose different name`,
              });
            }
          }
        }
      }
    }

    for (let i = 0; i < keys.length; i++) {
      let dietArray = [];

      if (
        keys[i] !== "from" &&
        keys[i] !== "to" &&
        //  /keys[i] !== "date" &&
        keys[i] !== "notes" &&
        keys[i] !== "clientName" &&
        keys[i] !== "dietChartId" &&
        keys[i] !== "weekPlan" &&
        keys[i] !== "chartType" &&
        keys[i] !== "createdAt" &&
        keys[i] !== "clientId" &&
        keys[i] !== "dietChartId"
      ) {
        for (let j = 0; j < dietPlan[keys[i]].length; j++) {
          let recipe = dietPlan[keys[i]];

          let data = {
            name: recipe[j].name?.label
              ? recipe[j].name?.value
              : recipe[j].name,
            andRecipe: recipe[j].andRecipe ?? null,
            alternativeName: recipe[j].alternativeName ?? null,
            ingredients: recipe[j].ingredients,
            groceryList: recipe[j].groceryList ?? [],
            nutritions: recipe[j].nutritions ?? [],
            description: recipe[j].description ? recipe[j].description : "",
            preparation: recipe[j].preparation ? recipe[j].preparation : "",
            howToTake: recipe[j].howToTake,
            foodCategory:
              recipe[j].foodCategory.value ?? recipe[j].foodCategory,
            calories: parseFloat(recipe[j].calories),
            weight: parseFloat(recipe[j].weight),
            foodType: recipe[j].foodType ?? [],
            unit: recipe[j].unit.value ?? recipe[j].unit,
            qty: recipe[j].qty,
            toggleMode:
              recipe[j].toggleMode && recipe[j].toggleMode !== ""
                ? recipe[j].toggleMode
                : "",
            image: recipe[j].image ? recipe[j].image : null,
          };
          // Handle image upload
          if (
            recipe[j].image &&
            typeof recipe[j].image !== "string" &&
            recipe[j].image instanceof File
          ) {
            const imageRef = ref(
              storage,
              `recipes/${uid}/${Date.now()}_${recipe[j].image.name}`
            );
            const snapshot = await uploadBytes(imageRef, recipe[j].image);
            const imageUrl = await getDownloadURL(snapshot.ref);
            data.image = imageUrl;
          }
          if (!recipe[j]["id"] && dietPlan[keys[i]][j]["name"]) {
            if (
              isNaN(data.calories) ||
              data.calories === "" ||
              data.calories === null
            ) {
              delete data.calories;
            }

            if (
              isNaN(data.calories) ||
              data.weight === "" ||
              data.weight === null
            ) {
              delete data.weight;
            }
            const recipeRef = await addDoc(collection(firestore, "recipe"), {
              ...data,
              createdAt: new Date(),
              adminId: uid,
            });

            dietArray.push({
              id: recipeRef.id,
              ...data,
              time: recipe[j].time,
            });
          } else {
            if (dietPlan[keys[i]][j]["name"]) {
              dietArray.push({
                id: recipe[j].id,
                ...data,
                time: recipe[j].time,
              });
            }
          }
        }

        if (dietArray.length > 0) {
          clientDietPlan = {
            ...clientDietPlan,
            [keys[i]]: dietArray,
          };
        }
      }
    }
    const dietPlanRef = await addDoc(collection(firestore, "dietplan"), {
      ...clientDietPlan,
    });

    // Send WhatsApp notification for diet chart creation
    try {
      await sendDietChartWhatsAppNotification(id, dietPlanRef.id, {
        sendNotification: true,
        templateId: WHATSAPP_TEMPLATE_TYPES.DIET_CHART_CREATED,
      });
    } catch (whatsappError) {
      console.warn("WhatsApp notification failed:", whatsappError);
      // Don't throw error for WhatsApp failure, just log it
    }

    return dietPlanRef.id;
  } catch (e) {
    console.log({ e });
    throw e;
  }
};

//Edit diet chart of client
export const EditDietChart = async ({ formdata, dietPlan, uid }) => {
  try {
    const storage = getStorage();
    const dietId = dietPlan.dietChartId;
    const dietRef = doc(firestore, "dietplan", dietId);

    let clientDietPlan = {
      clientId: formdata.clientName.value,
      date: dietPlan.date,
      from: dietPlan.from,
      to: dietPlan.to,
      notes: dietPlan.notes,
      weekPlan: dietPlan.weekPlan?.value,
      chartType: dietPlan.chartType?.value,
      createdAt: dietPlan.createdAt ?? new Date(),
    };

    let keys = Object.keys(dietPlan);

    for (let i = 0; i < keys.length; i++) {
      if (
        keys[i] !== "from" &&
        keys[i] !== "to" &&
        keys[i] !== "date" &&
        keys[i] !== "notes" &&
        keys[i] !== "clientName" &&
        keys[i] !== "createdAt" &&
        keys[i] !== "dietChartId" &&
        keys[i] !== "weekPlan" &&
        keys[i] !== "chartType" &&
        keys[i] !== "clientId"
      ) {
        for (let j = 0; j < dietPlan[keys[i]].length; j++) {
          let recipe = dietPlan[keys[i]];

          if (!dietPlan[keys[i]][j]["id"] && dietPlan[keys[i]][j]["name"]) {
            const recipeName =
              typeof recipe[j].name === "object"
                ? recipe[j].name?.value
                : recipe[j].name;

            if (!recipeName || typeof recipeName !== "string") {
              continue;
            }
            const q = query(
              collection(firestore, "recipe"),
              where("name", "==", recipeName),
              where("adminId", "==", uid)
            );
            const querySnapshotv1 = await getDocs(q);
            if (!querySnapshotv1.empty) {
              throw new Error({
                message: `Recipe with this name ${recipe[j].name?.value} already exists, choose different name`,
              });
            }
          }
        }
      }
    }

    for (let i = 0; i < keys.length; i++) {
      let dietArray = [];

      if (
        keys[i] !== "from" &&
        keys[i] !== "to" &&
        keys[i] !== "date" &&
        keys[i] !== "notes" &&
        keys[i] !== "clientName" &&
        keys[i] !== "dietChartId" &&
        keys[i] !== "weekPlan" &&
        keys[i] !== "chartType" &&
        keys[i] !== "createdAt" &&
        keys[i] !== "clientId"
      ) {
        for (let j = 0; j < dietPlan[keys[i]].length; j++) {
          let recipe = dietPlan[keys[i]];
          let data = {
            name: recipe[j].name?.label
              ? recipe[j].name?.value
              : recipe[j].name,
            ingredients: recipe[j].ingredients,
            groceryList: recipe[j].groceryList ?? [],
            nutritions: recipe[j].nutritions ?? [],
            description: recipe[j].description ?? "N/A",
            preparation: recipe[j].preparation,
            howToTake: recipe[j].howToTake,
            foodCategory:
              recipe[j].foodCategory?.value ?? recipe[j].foodCategory,
            calories: parseFloat(recipe[j].calories),
            weight: parseFloat(recipe[j].weight),
            foodType: recipe[j].foodType ?? [],
            unit: recipe[j].unit?.value ?? recipe[j].unit,
            qty: recipe[j].qty,
            toggleMode:
              recipe[j].toggleMode && recipe[j].toggleMode !== ""
                ? recipe[j].toggleMode
                : "", // Ensure toggleType is included
            image: recipe[j].image ? recipe[j].image : null,
          };

          if (
            recipe[j].image &&
            typeof recipe[j].image !== "string" &&
            recipe[j].image instanceof File
          ) {
            const imageRef = ref(
              storage,
              `recipes/${uid}/${Date.now()}_${recipe[j].image.name}`
            );
            const snapshot = await uploadBytes(imageRef, recipe[j].image);
            const imageUrl = await getDownloadURL(snapshot.ref);
            data.image = imageUrl;
          }

          if (!recipe[j]["id"] && dietPlan[keys[i]][j]["name"]) {
            if (
              isNaN(data.calories) ||
              data.calories === "" ||
              data.calories === null
            ) {
              delete data.calories;
            }

            if (
              isNaN(data.calories) ||
              data.weight === "" ||
              data.weight === null
            ) {
              delete data.weight;
            }

            const recipeRef = await addDoc(collection(firestore, "recipe"), {
              ...data,
              createdAt: new Date(),
              adminId: uid,
            });

            dietArray.push({
              id: recipeRef.id,
              ...data,
              time: recipe[j].time,
            });
          } else {
            if (dietPlan[keys[i]][j]["name"]) {
              dietArray.push({
                id: recipe[j].id,
                ...data,
                time: recipe[j].time,
              });
            }
          }
        }

        if (dietArray.length > 0) {
          clientDietPlan = {
            ...clientDietPlan,
            dietChartId: dietPlan.dietChartId,
            [keys[i]]: dietArray,
          };
        }
      }
    }

    await updateDoc(dietRef, {
      ...clientDietPlan,
    });
    return { ...clientDietPlan, dietChartId: dietId };
  } catch (e) {
    console.log(e);
    throw e;
  }
};

export const getClientKey = async ({ id }) => {
  try {
    let data = [];

    const ref = doc(firestore, "client", id);

    const snapshot = await getDoc(ref);
    if (snapshot.exists()) {
      data = snapshot.data();
      data = {
        key: id,
        label: snapshot.data().name,
        name: snapshot.data().name,
        note: "",
        phoneNumber: snapshot.data().phoneNumber,
        value: id,
      };
      return data;
    } else {
      throw new Error({ message: "Client not found!!!" });
    }
  } catch (e) {
    throw e;
  }
};

//  export async function uploadImageAndGetURL(file, uid, index) {
//   const storageRef = ref(storage, `dietImages/${uid}/${Date.now()}_${index}_${file.name}`);
//   await uploadBytes(storageRef, file);
//   const url = await getDownloadURL(storageRef);
//   return url;
// }

// Send WhatsApp notification for diet chart creation
export const sendDietChartWhatsAppNotification = async () => {
  return {
    success: false,
    message:
      "Diet chart WhatsApp is disabled. smsmenow was removed and no CallDesk diet-chart template is approved yet.",
  };
};

// Send bulk WhatsApp notifications for diet chart creation
export const sendBulkDietChartWhatsAppNotifications = async (
  clientIds,
  options = {}
) => {
  try {
    const {
      sendNotification = true,
      templateId = null,
      dietChartUrl = null,
    } = options;

    if (!sendNotification || !clientIds || clientIds.length === 0) {
      return {
        success: false,
        message: "No clients to notify or notification disabled",
      };
    }

    const results = [];

    for (const clientId of clientIds) {
      const result = await sendDietChartWhatsAppNotification(clientId, null, {
        sendNotification,
        templateId,
        dietChartUrl,
      });
      results.push({ clientId, ...result });
    }

    const successCount = results.filter((r) => r.success).length;
    const failureCount = results.length - successCount;

    return {
      success: failureCount === 0,
      results,
      summary: {
        total: results.length,
        successful: successCount,
        failed: failureCount,
      },
      message: `WhatsApp notifications sent: ${successCount} successful, ${failureCount} failed`,
    };
  } catch (error) {
    console.error(
      "Error sending bulk diet chart WhatsApp notifications:",
      error
    );
    return {
      success: false,
      error: error.message,
      message: "Failed to send bulk WhatsApp notifications",
    };
  }
};
